import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Users, 
  UserPlus, 
  MoreHorizontal, 
  Shield, 
  ShieldCheck, 
  ShieldAlert,
  Eye,
  Mail,
  Calendar,
  CheckCircle2,
  XCircle,
  Crown,
  Clock,
  RefreshCw,
  Trash2,
  Send,
  UserCheck,
  UserX,
  AlertTriangle
} from 'lucide-react';

const ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full access to all features', icon: Crown, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  { value: 'finance', label: 'Finance', description: 'Manage transactions, approvals, reconciliation', icon: ShieldCheck, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  { value: 'accountant', label: 'Accountant', description: 'Audit logs, period management, detailed reports', icon: Shield, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access to all data', icon: Eye, color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
];

const getRoleInfo = (role) => ROLES.find(r => r.value === role) || ROLES[3];

const getStatusBadge = (status, isActive) => {
  if (!isActive) {
    return (
      <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-0">
        <UserX size={12} className="mr-1" />
        Inactive
      </Badge>
    );
  }
  
  switch (status) {
    case 'accepted':
      return (
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-0">
          <UserCheck size={12} className="mr-1" />
          Active
        </Badge>
      );
    case 'pending':
      return (
        <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-0">
          <Clock size={12} className="mr-1" />
          Pending
        </Badge>
      );
    default:
      return (
        <Badge className="bg-slate-100 text-slate-600 border-0">
          {status}
        </Badge>
      );
  }
};

const TeamManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total_users: 0, active_users: 0, pending_invitations: 0, inactive_users: 0 });
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [reinviting, setReinviting] = useState(null);
  
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'viewer'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        api.get('/users'),
        api.get('/users/stats').catch(() => ({ data: null }))
      ]);
      setUsers(usersRes.data);
      if (statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (error) {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users/invite', inviteForm);
      toast.success(
        `Invitation sent to ${inviteForm.email}! They will receive an email with login credentials.`,
        { duration: 5000 }
      );
      setInviteDialogOpen(false);
      setInviteForm({ email: '', name: '', role: 'viewer' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    }
  };

  const handleReinvite = async (userId) => {
    setReinviting(userId);
    try {
      const response = await api.post(`/users/${userId}/reinvite`);
      toast.success(response.data.message || 'Invitation resent successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setReinviting(null);
    }
  };

  const handleRevokeInvitation = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await api.delete(`/users/${selectedUser.id}/invitation`);
      toast.success(response.data.message || 'Invitation revoked');
      setRevokeDialogOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke invitation');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}`, { role: newRole });
      toast.success('User role updated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await api.put(`/users/${userId}`, { is_active: !isActive });
      toast.success(isActive ? 'User deactivated' : 'User activated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const isAdmin = user?.role === 'admin';

  // Filter users based on active tab
  const filteredUsers = users.filter(u => {
    switch (activeTab) {
      case 'active':
        return u.invitation_status === 'accepted' && u.is_active;
      case 'pending':
        return u.invitation_status === 'pending' && u.is_active;
      case 'inactive':
        return !u.is_active;
      default:
        return true;
    }
  });

  const pendingCount = users.filter(u => u.invitation_status === 'pending' && u.is_active).length;

  return (
    <div className="space-y-6" data-testid="team-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            Team Management
          </h1>
          <p className="text-[hsl(var(--muted-ink))] mt-1">
            Manage team members and their access levels
          </p>
        </div>
        {isAdmin && (
          <Button 
            onClick={() => setInviteDialogOpen(true)}
            className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))]"
            data-testid="invite-user-btn"
          >
            <UserPlus size={18} className="mr-2" />
            Invite Member
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white/80 dark:bg-slate-900/80 backdrop-blur">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                <Users size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_users}</p>
                <p className="text-xs text-[hsl(var(--muted-ink))]">Total Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/80 dark:bg-slate-900/80 backdrop-blur">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
                <UserCheck size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active_users}</p>
                <p className="text-xs text-[hsl(var(--muted-ink))]">Active Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/80 dark:bg-slate-900/80 backdrop-blur">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400">
                <Clock size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending_invitations}</p>
                <p className="text-xs text-[hsl(var(--muted-ink))]">Pending Invites</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/80 dark:bg-slate-900/80 backdrop-blur">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                <UserX size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.inactive_users}</p>
                <p className="text-xs text-[hsl(var(--muted-ink))]">Inactive</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Role Legend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield size={18} />
            Role Permissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {ROLES.map((role) => (
              <div key={role.value} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                <div className={`p-2 rounded-lg ${role.color}`}>
                  <role.icon size={16} />
                </div>
                <div>
                  <p className="font-medium text-sm">{role.label}</p>
                  <p className="text-xs text-[hsl(var(--muted-ink))]">{role.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Team Members Table with Tabs */}
      <Card>
        <CardHeader className="pb-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <Users size={20} />
              Team Members
            </CardTitle>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all" className="text-xs sm:text-sm">
                  All ({users.length})
                </TabsTrigger>
                <TabsTrigger value="active" className="text-xs sm:text-sm">
                  Active ({stats.active_users})
                </TabsTrigger>
                <TabsTrigger value="pending" className="text-xs sm:text-sm relative">
                  Pending
                  {pendingCount > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                      {pendingCount}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="inactive" className="text-xs sm:text-sm">
                  Inactive ({stats.inactive_users})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto mb-4 text-[hsl(var(--muted-ink))] opacity-50" size={40} />
              <p className="text-[hsl(var(--muted-ink))]">
                {activeTab === 'pending' ? 'No pending invitations' :
                 activeTab === 'inactive' ? 'No inactive users' :
                 activeTab === 'active' ? 'No active users' :
                 'No team members yet'}
              </p>
              {activeTab === 'all' && isAdmin && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setInviteDialogOpen(true)}
                >
                  <UserPlus size={16} className="mr-2" />
                  Invite your first team member
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="hidden md:table-cell">Invited</TableHead>
                    <TableHead>Status</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((member) => {
                    const roleInfo = getRoleInfo(member.role);
                    const isCurrentUser = member.id === user?.id;
                    const isPending = member.invitation_status === 'pending';
                    
                    return (
                      <TableRow key={member.id} data-testid={`user-row-${member.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                              isPending 
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' 
                                : 'bg-[hsl(var(--brand-100))] text-[hsl(var(--brand-700))]'
                            }`}>
                              {member.name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="font-medium flex items-center gap-2">
                                {member.name}
                                {isCurrentUser && (
                                  <Badge variant="outline" className="text-xs">You</Badge>
                                )}
                              </p>
                              <p className="text-sm text-[hsl(var(--muted-ink))] flex items-center gap-1">
                                <Mail size={12} />
                                {member.email}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${roleInfo.color} border-0`}>
                            <roleInfo.icon size={12} className="mr-1" />
                            {roleInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <span className="text-sm text-[hsl(var(--muted-ink))] flex items-center gap-1">
                            <Calendar size={12} />
                            {member.invited_at 
                              ? new Date(member.invited_at).toLocaleDateString()
                              : '-'
                            }
                          </span>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(member.invitation_status, member.is_active)}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            {!isCurrentUser && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" data-testid={`user-actions-${member.id}`}>
                                    <MoreHorizontal size={16} />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {isPending && (
                                    <>
                                      <DropdownMenuItem
                                        onClick={() => handleReinvite(member.id)}
                                        disabled={reinviting === member.id}
                                      >
                                        {reinviting === member.id ? (
                                          <RefreshCw size={14} className="mr-2 animate-spin" />
                                        ) : (
                                          <Send size={14} className="mr-2" />
                                        )}
                                        Resend Invitation
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => {
                                          setSelectedUser(member);
                                          setRevokeDialogOpen(true);
                                        }}
                                        className="text-rose-600"
                                      >
                                        <Trash2 size={14} className="mr-2" />
                                        Revoke Invitation
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                    </>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedUser(member);
                                      setEditDialogOpen(true);
                                    }}
                                  >
                                    <Shield size={14} className="mr-2" />
                                    Change Role
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleToggleActive(member.id, member.is_active)}
                                  >
                                    {member.is_active ? (
                                      <>
                                        <XCircle size={14} className="mr-2" />
                                        Deactivate
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle2 size={14} className="mr-2" />
                                        Activate
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Add a new member to your team. They will receive an email with their login credentials.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-name">Name</Label>
              <Input
                id="invite-name"
                placeholder="John Doe"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                required
                data-testid="invite-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="john@company.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                required
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
              >
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center gap-2">
                        <role.icon size={14} />
                        <span>{role.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[hsl(var(--muted-ink))]">
                {ROLES.find(r => r.value === inviteForm.role)?.description}
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="send-invite-btn">
                <Send size={16} className="mr-2" />
                Send Invitation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Update the role for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select New Role</Label>
              <div className="space-y-2">
                {ROLES.map((role) => (
                  <button
                    key={role.value}
                    type="button"
                    onClick={() => {
                      handleUpdateRole(selectedUser?.id, role.value);
                      setEditDialogOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
                      selectedUser?.role === role.value
                        ? 'border-[hsl(var(--brand-500))] bg-[hsl(var(--brand-50))]'
                        : 'border-transparent bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700'
                    }`}
                    data-testid={`role-option-${role.value}`}
                  >
                    <div className={`p-2 rounded-lg ${role.color}`}>
                      <role.icon size={16} />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-sm">{role.label}</p>
                      <p className="text-xs text-[hsl(var(--muted-ink))]">{role.description}</p>
                    </div>
                    {selectedUser?.role === role.value && (
                      <CheckCircle2 size={18} className="ml-auto text-[hsl(var(--brand-500))]" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revoke Invitation Confirmation Dialog */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-rose-500" size={20} />
              Revoke Invitation?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the invitation for <strong>{selectedUser?.email}</strong>. 
              They will no longer be able to join your team with this invitation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedUser(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeInvitation}
              className="bg-rose-600 hover:bg-rose-700"
            >
              <Trash2 size={16} className="mr-2" />
              Revoke Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamManagement;
